import { useState, useEffect } from 'react';
import { Heart, User, ChevronDown, ChevronUp } from 'lucide-react';
import { timeAgo, formatCount } from '@/lib/formatters';
import { getPostContent } from '@/lib/postUtils';
import { getPostUser, getLikesCount } from '@/components/PostCard';
import { useLikePost } from '@/hooks/usePost';
import type { Post } from '@/types';

export interface CommentItemProps {
  comment: Post;
  allComments?: Post[];
  depth?: number;
  onProfileClick?: (username: string) => void;
  onReply?: (comment: Post) => void;
}


export default function CommentItem({ comment, allComments, depth = 0, onProfileClick, onReply }: CommentItemProps) {
  const [showReplies, setShowReplies] = useState(false);
  const author = getPostUser(comment);
  const content = getPostContent(comment);
  const time = comment.createdAt ? timeAgo(comment.createdAt) : '';
  const baseLikes = getLikesCount(comment);

  // Optimistic like state - check userReaction for 'heart' (comments use userReaction, not liked)
  const isLiked = comment.postEngagement?.userReaction === 'heart';
  const [liked, setLiked] = useState(isLiked);
  const [likesCount, setLikesCount] = useState(baseLikes);

  const likeMutation = useLikePost();

  // Sync with server state when comment data changes
  useEffect(() => {
    setLiked(comment.postEngagement?.userReaction === 'heart');
    setLikesCount(getLikesCount(comment));
  }, [comment]);

  const handleLike = () => {
    const commentUlid = comment.ulid ?? comment.id;
    if (!commentUlid) return;

    // Optimistic update
    setLiked((prev) => !prev);
    setLikesCount((prev) => liked ? Math.max(0, prev - 1) : prev + 1);

    likeMutation.mutate({ ulid: commentUlid, liked });
  };

  const childComments = allComments ? allComments.filter(c => (c as Record<string, unknown>).parentUlid === comment.ulid) : [];

  return (
    <div className="flex gap-3 py-3">
      <div
        onClick={() => author.username && onProfileClick?.(author.username)}
        className="cursor-pointer flex-shrink-0"
      >
        {author.avatar ? (
          <img
            src={author.avatar}
            alt={author.name}
            className="w-9 h-9 rounded-full object-cover mt-0.5"
          />
        ) : (
          <div className="w-9 h-9 rounded-full bg-[#141821] flex items-center justify-center mt-0.5">
            <User size={16} className="text-[#A7B0C8]" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span
            onClick={() => author.username && onProfileClick?.(author.username)}
            className="text-sm font-semibold text-[#F5F7FF] cursor-pointer hover:underline"
          >
            {author.name}
          </span>
          {author.username && (
            <span
              onClick={() => onProfileClick?.(author.username)}
              className="text-xs text-[#A7B0C8] cursor-pointer hover:underline"
            >
              @{author.username}
            </span>
          )}
          <span className="text-xs text-[#A7B0C8]">{time}</span>
        </div>
        <p className="text-sm text-[#E0E4F0] mt-0.5 whitespace-pre-line">{content}</p>
        <div className="flex items-center gap-4 mt-2">
          <button
            className={`flex items-center gap-1 text-xs transition-colors ${liked ? 'text-[#FF4D4D]' : 'text-[#A7B0C8] hover:text-[#F5F7FF]'}`}
            onClick={handleLike}
          >
            <Heart size={14} fill={liked ? '#FF4D4D' : 'none'} />
            <span>{formatCount(likesCount)}</span>
          </button>
          {onReply && (
            <button
              onClick={() => onReply(comment)}
              className="text-xs text-[#A7B0C8] hover:text-[#F5F7FF] transition-colors"
            >
              Reply
            </button>
          )}
        </div>

        {/* Nested replies toggle */}
        {childComments.length > 0 && (
          <button
            onClick={() => setShowReplies(!showReplies)}
            className="flex items-center gap-1 mt-2 text-xs text-[#A38BFF] hover:text-[#B9A5FF] transition-colors"
          >
            {showReplies ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            <span>
              {showReplies
                ? 'Hide replies'
                : `Show ${childComments.length} ${childComments.length === 1 ? 'reply' : 'replies'}`}
            </span>
          </button>
        )}

        {/* Nested replies */}
        {childComments.length > 0 && showReplies && (
          <div className="pl-4 border-l-2 border-white/10 mt-2">
            {childComments.map((child) => (
              <CommentItem
                key={child.ulid ?? child.id}
                comment={child}
                allComments={allComments}
                depth={depth + 1}
                onProfileClick={onProfileClick}
                onReply={onReply}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
